import React, { useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix default marker icons broken by webpack asset hashing
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl:       require('leaflet/dist/images/marker-icon.png'),
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  shadowUrl:     require('leaflet/dist/images/marker-shadow.png'),
});

// ─── Category emoji map ────────────────────────────────────────────────────
const CATEGORY_ICONS = {
  plants_medicine: '🌿',
  hunting:         '🦌',
  birds:           '🦅',
  fishing:         '🎣',
  environmental:   '⚠️',
  other:           '📍',
};

const STATUS_COLORS = {
  submitted: '#f59e0b',
  approved:  '#22c55e',
  rejected:  '#ef4444',
  draft:     '#9ca3af',
};

function makeIcon(emoji, statusColor) {
  const html = `
    <div style="
      font-size:24px;
      line-height:1;
      text-shadow:0 0 4px #fff,0 0 8px #fff;
      filter:drop-shadow(0 2px 3px rgba(0,0,0,.35));
    ">${emoji}</div>
    <div style="
      width:10px;height:10px;
      border-radius:50%;
      background:${statusColor};
      border:2px solid #fff;
      margin:-4px auto 0;
      box-shadow:0 1px 3px rgba(0,0,0,.4);
    "></div>`;
  return L.divIcon({
    html,
    className: '',
    iconSize:  [36, 46],
    iconAnchor:[18, 46],
    popupAnchor:[0, -46],
  });
}

// Re-centre map when bounds change
function BoundsUpdater({ records }) {
  const map = useMap();
  useEffect(() => {
    const pts = records
      .filter((r) => r.location?.coordinates?.length === 2)
      .map((r) => [r.location.coordinates[1], r.location.coordinates[0]]);
    if (pts.length > 0) {
      const bounds = L.latLngBounds(pts);
      map.fitBounds(bounds, { maxZoom: 13, padding: [40, 40] });
    }
  }, [records, map]);
  return null;
}

const SEASON_LABEL = { spring: 'Spring', summer: 'Summer', fall: 'Fall', winter: 'Winter' };
const STATUS_LABEL = { submitted: 'Pending review', approved: 'Approved', rejected: 'Rejected', draft: 'Draft' };

/**
 * HarvestMap
 * Props:
 *   records      — array of HarvestRecord objects fetched from the API
 *   center       — [lat, lng] default centre (falls back to [55, -100])
 *   zoom         — default zoom level (falls back to 5)
 *   onRecordClick— optional callback(record) when a popup action is clicked
 *   readonly     — if true, no review actions shown
 */
export default function HarvestMap({
  records = [],
  center = [55, -100],
  zoom   = 5,
  onRecordClick,
  readonly = false,
}) {
  const mapped = records.filter((r) => r.location?.coordinates?.length === 2);

  return (
    <div style={{ height: 420, borderRadius: 8, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
      <MapContainer
        center={center}
        zoom={zoom}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {mapped.length > 0 && <BoundsUpdater records={mapped} />}

        {mapped.map((record) => {
          const [lng, lat] = record.location.coordinates;
          const emoji = CATEGORY_ICONS[record.category] || '📍';
          const color = STATUS_COLORS[record.status]    || '#9ca3af';
          const icon  = makeIcon(emoji, color);
          const submittedBy = record.userId?.name || 'Unknown';

          return (
            <Marker key={record._id} position={[lat, lng]} icon={icon}>
              <Popup minWidth={220}>
                <div style={{ lineHeight: 1.6, fontSize: 13 }}>
                  <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>
                    {emoji} {record.species || record.category}
                  </div>
                  {record.subcategory && (
                    <div style={{ color: '#6b7280' }}>{record.subcategory}</div>
                  )}
                  <div>
                    <strong>Season:</strong> {SEASON_LABEL[record.season] || record.season}
                  </div>
                  <div>
                    <strong>Date:</strong>{' '}
                    {new Date(record.harvestDate).toLocaleDateString()}
                  </div>
                  {record.quantity && (
                    <div>
                      <strong>Quantity:</strong> {record.quantity} {record.unit}
                    </div>
                  )}
                  {record.abundance && (
                    <div>
                      <strong>Abundance:</strong> {record.abundance.replace('_', ' ')}
                    </div>
                  )}
                  {record.observations && (
                    <div style={{ marginTop: 4 }}>
                      <strong>Notes:</strong>{' '}
                      <span style={{ color: '#374151' }}>{record.observations}</span>
                    </div>
                  )}
                  {record.locationDescription && (
                    <div>
                      <strong>Location:</strong> {record.locationDescription}
                    </div>
                  )}
                  <div style={{ marginTop: 6, color: '#6b7280', fontSize: 12 }}>
                    <strong>By:</strong> {submittedBy} &nbsp;·&nbsp;{' '}
                    <span
                      style={{
                        background: color,
                        color: '#fff',
                        borderRadius: 4,
                        padding: '1px 6px',
                        fontSize: 11,
                      }}
                    >
                      {STATUS_LABEL[record.status] || record.status}
                    </span>
                  </div>
                  {!readonly && onRecordClick && (
                    <button
                      onClick={() => onRecordClick(record)}
                      style={{
                        marginTop: 8,
                        padding: '4px 10px',
                        background: '#1d4ed8',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 4,
                        cursor: 'pointer',
                        fontSize: 12,
                      }}
                    >
                      Review
                    </button>
                  )}
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>
    </div>
  );
}
